import styled from "styled-components";
import moment from "moment";
import { Grid, Typography } from "@material-ui/core";
import StateLabel from "../../components/StateLabel";
import { ReactComponent as RightArrowIcon } from "../../../assets/icons/right-arrow.svg";
import { translate as t } from "../../../infrastructure/translate/localization";
import { MonetaryAmount } from "../../domain/monetary-amount";

const StyledRightArrow = styled(RightArrowIcon)`
  margin-left: ${window.innerWidth <= 1440 ? `4px` : `13px`};
`;

const StyledTypography = styled(Typography)`
  && {
    font-size: 10px;
    color: #929fad;
  }
`;

const parseCell = (cellData) => {
  const isLaptop = window.innerWidth <= 1440;
  let parsedCell = "";
  if (cellData.length > 27 && !isLaptop) {
    parsedCell = `${cellData.substring(0, 27)}...`;
  } else if (cellData.length > 13 && isLaptop) {
    parsedCell = `${cellData.substring(0, 13)}...`;
  } else {
    parsedCell = cellData;
  }

  return parsedCell;
};

export const configStates = {
  default: {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.confirm"),
    rejected: t("recurrent-payments-section.rejected"),
    finished: t("recurrent-payments-section.finished"),
    cancelled: t("recurrent-payments-section.cancelled"),
  },
  nursery: {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.confirm"),
    rejected: t("recurrent-payments-section.rejected"),
    finished: t("recurrent-payments-section.finished"),
    cancelled: t("recurrent-payments-section.cancelled"),
  },
  education: {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.confirm"),
    rejected: t("recurrent-payments-section.rejected"),
    finished: t("recurrent-payments-section.finished"),
    cancelled: t("recurrent-payments-section.cancelled"),
  },
  "health-insurance": {
    confirm: t("recurrent-payments-section.subscribe"),
    cancelled: t("recurrent-payments-section.unsubscribe"),
    finished: t("recurrent-payments-section.unsubscribe"),
  },
  "life-insurance": {
    confirm: t("recurrent-payments-section.subscribe"),
    cancelled: t("recurrent-payments-section.unsubscribe"),
    finished: t("recurrent-payments-section.unsubscribe"),
  },
  renting: {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.confirm"),
    rejected: t("recurrent-payments-section.rejected"),
    finished: t("recurrent-payments-section.finished"),
    cancelled: t("recurrent-payments-section.cancelled"),
  },
  wellness: {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.subscribe"),
    cancelled: t("recurrent-payments-section.unsubscribe"),
  },
  "retirement-insurance": {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.subscribe"),
    cancelled: t("recurrent-payments-section.unsubscribe"),
  },
  "pension-plan": {
    pending: t("card-states.pending"),
    confirm: t("recurrent-payments-section.subscribe"),
    cancelled: t("recurrent-payments-section.unsubscribe"),
  },
};

const StateColors = (isInsurance: boolean) => ({
  confirm: "green",
  pending: "yellow",
  cancelled: "red",
  rejected: "darkRed",
  finished: isInsurance ? "red" : "grey",
});

export const parseStateColor = ({
  state,
  isInsurance = false,
}: {
  state: string;
  isInsurance?: boolean;
}): string => StateColors(isInsurance)[state];

export const recurrentPaymentConfig = {
  default: {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.confirm") },
      { value: "denied", name: t("recurrent-payments-section.rejected") },
      { value: "cancelled", name: t("recurrent-payments-section.cancelled") },
      { value: "finished", name: t("recurrent-payments-section.finished") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "nursery",
        label: t("benefits-section.nursery-benefit.nursery"),
      },
      {
        dataKey: "amount",
        label: t("recurrent-payments-section.fee-per-month"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    hasRequest: false,
    parseData: (data, type) =>
      data.map(
        ({ id, employee, state, requestDate, amount, nursery, ...rest }) => ({
          id,
          name: parseCell(`${employee.name} ${employee.surname}`),
          legalId: employee.legalId,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          nursery: parseCell(nursery),
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
  },
  nursery: {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.confirm") },
      { value: "denied", name: t("recurrent-payments-section.rejected") },
      { value: "cancelled", name: t("recurrent-payments-section.cancelled") },
      { value: "finished", name: t("recurrent-payments-section.finished") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "nursery",
        label: t("benefits-section.nursery-benefit.nursery"),
      },
      {
        dataKey: "amount",
        label: t("recurrent-payments-section.fee-per-month"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    hasRequest: false,
    parseData: (data, type) =>
      data.map(
        ({ id, employee, state, requestDate, amount, nursery, ...rest }) => ({
          id,
          name: parseCell(`${employee.name} ${employee.surname}`),
          legalId: employee.legalId,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          nursery: parseCell(nursery),
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
  },
  education: {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.confirm") },
      { value: "denied", name: t("recurrent-payments-section.rejected") },
      { value: "cancelled", name: t("recurrent-payments-section.cancelled") },
      { value: "finished", name: t("recurrent-payments-section.finished") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "course",
        label: t("recurrent-payments-section.course"),
      },
      {
        dataKey: "amount",
        label: t("common-messages.price"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({ id, employee, state, requestDate, amount, course, ...rest }) => ({
          id,
          name: parseCell(`${employee.name} ${employee.surname}`),
          legalId: employee.legalId,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          course: parseCell(course),
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
  },
  "health-insurance": {
    states: [
      { value: "started", name: t("recurrent-payments-section.subscribe") },
      { value: "cancelled", name: t("recurrent-payments-section.unsubscribe") },
    ],
    modalityFilter: true,
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "modality",
        label: t("recurrent-payments-section.modality"),
      },
      {
        dataKey: "insuranceCarrier",
        label: t("recurrent-payments-section.insurance-carrier"),
      },
      {
        dataKey: "amount",
        label: t("recurrent-payments-section.fee-per-month"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({
          id,
          employee,
          recipient,
          requestDate,
          amount,
          state,
          modality,
          insuranceCarrier,
          ...rest
        }) => ({
          id,
          name:
            recipient.type === "employee" ? (
              parseCell(`${recipient.name} ${recipient.surname}`)
            ) : (
              <Grid>
                {parseCell(`${recipient.name} ${recipient.surname}`)}
                <StyledTypography
                  title={`${employee.name} ${employee.surname}`}
                >
                  {`${t("common-messages.employee")}: ${parseCell(
                    `${employee.name} ${employee.surname}`
                  )}`}
                </StyledTypography>
              </Grid>
            ),
          legalId: recipient.legalId || "-",
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          modality: parseCell(modality),
          insuranceCarrier: parseCell(insuranceCarrier),
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state, isInsurance: true })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
  },
  "life-insurance": {
    states: [
      { value: "started", name: t("recurrent-payments-section.subscribe") },
      { value: "cancelled", name: t("recurrent-payments-section.unsubscribe") },
    ],
    modalityFilter: true,
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "modality",
        label: t("recurrent-payments-section.modality"),
      },
      {
        dataKey: "insuranceCarrier",
        label: t("recurrent-payments-section.insurance-carrier"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({
          id,
          state,
          employee,
          requestDate,
          modality,
          insuranceCarrier,
          ...rest
        }) => ({
          id,
          name: `${employee.name} ${employee.surname}`,
          legalId: employee.legalId,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          modality: parseCell(modality),
          insuranceCarrier: parseCell(insuranceCarrier),
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state, isInsurance: true })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
  },
  renting: {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.confirm") },
      { value: "denied", name: t("recurrent-payments-section.rejected") },
      { value: "cancelled", name: t("recurrent-payments-section.cancelled") },
      { value: "finished", name: t("recurrent-payments-section.finished") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "brand",
        label: t("recurrent-payments-section.brand"),
      },
      {
        dataKey: "amount",
        label: t("common-messages.amount"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({ id, employee, requestDate, state, amount, brand, ...rest }) => ({
          id,
          name: parseCell(`${employee.name} ${employee.surname}`),
          legalId: employee.legalId,
          brand: parseCell(brand),
          amount:
            amount.amountInCents === 0
              ? "-"
              : `${MonetaryAmount.ofCurrency({
                  amountInCents: amount.amountInCents,
                  currency: amount.currency,
                }).prettifyMoneyWithoutSymbol()}€`,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
  },
  wellness: {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.subscribe") },
      { value: "cancelled", name: t("recurrent-payments-section.unsubscribe") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "productName",
        label: t("recurrent-payments-section.product"),
      },
      {
        dataKey: "amount",
        label: t("recurrent-payments-section.fee-per-month"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({
          id,
          employee,
          recipient,
          requestDate,
          amount,
          state,
          modality,
          ...rest
        }) => ({
          id,
          name:
            recipient.type === "employee" ? (
              parseCell(`${recipient.name} ${recipient.surname}`)
            ) : (
              <Grid>
                {parseCell(`${recipient.name} ${recipient.surname}`)}
                <StyledTypography
                  title={`${employee.name} ${employee.surname}`}
                >
                  {`${t("common-messages.employee")}: ${parseCell(
                    `${employee.name} ${employee.surname}`
                  )}`}
                </StyledTypography>
              </Grid>
            ),
          legalId: recipient.legalId || "-",
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          modality: parseCell(modality),
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
  },
  "retirement-insurance": {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.subscribe") },
      { value: "cancelled", name: t("recurrent-payments-section.unsubscribe") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "productName",
        label: t("recurrent-payments-section.product"),
      },
      {
        dataKey: "amount",
        label: t("recurrent-payments-section.monthly-contribution"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({ id, employee, recipient, requestDate, amount, state, ...rest }) => ({
          id,
          name:
            recipient.type === "employee" ? (
              parseCell(`${recipient.name} ${recipient.surname}`)
            ) : (
              <Grid>
                {parseCell(`${recipient.name} ${recipient.surname}`)}
                <StyledTypography
                  title={`${employee.name} ${employee.surname}`}
                >
                  {`${t("common-messages.employee")}: ${parseCell(
                    `${employee.name} ${employee.surname}`
                  )}`}
                </StyledTypography>
              </Grid>
            ),
          legalId: recipient.legalId || "-",
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
    hideDetails: true,
  },
  "pension-plan": {
    states: [
      { value: "pending", name: t("card-states.pending") },
      { value: "started", name: t("recurrent-payments-section.subscribe") },
      { value: "cancelled", name: t("recurrent-payments-section.unsubscribe") },
    ],
    columns: [
      {
        dataKey: "legalId",
        label: "DNI/NIE",
      },
      {
        dataKey: "name",
        label: t("common-messages.name"),
      },
      {
        dataKey: "productName",
        label: t("recurrent-payments-section.product"),
      },
      {
        dataKey: "amount",
        label: t("recurrent-payments-section.monthly-contribution"),
      },
      {
        dataKey: "requestDate",
        label: t("recurrent-payments-section.request"),
      },
      {
        dataKey: "actions",
        label: "",
      },
    ],
    parseData: (data, type) =>
      data.map(
        ({ id, employee, recipient, requestDate, amount, state, ...rest }) => ({
          id,
          name:
            recipient.type === "employee" ? (
              parseCell(`${recipient.name} ${recipient.surname}`)
            ) : (
              <Grid>
                {parseCell(`${recipient.name} ${recipient.surname}`)}
                <StyledTypography
                  title={`${employee.name} ${employee.surname}`}
                >
                  {`${t("common-messages.employee")}: ${parseCell(
                    `${employee.name} ${employee.surname}`
                  )}`}
                </StyledTypography>
              </Grid>
            ),
          legalId: recipient.legalId || "-",
          amount: `${MonetaryAmount.ofCurrency({
            amountInCents: amount.amountInCents,
            currency: amount.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          requestDate: `${moment(requestDate).format("D")} ${moment(
            requestDate
          ).format("MMM")} ${moment(requestDate).format("YYYY")}`,
          actions: (
            <Grid container alignItems="center" justifyContent="flex-end">
              <Grid item>
                <StateLabel
                  text={
                    type in configStates
                      ? configStates[type][state]
                      : configStates.default[state]
                  }
                  color={parseStateColor({ state })}
                />
              </Grid>
            </Grid>
          ),
          ...rest,
        })
      ),
    hasRequest: true,
    hideDetails: true,
  },
};
